import { ArrowRight, Heart, MapPin, Clock } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { TEMPLATES } from "@/lib/wedding";
import { BrandLogo, BrandIcon } from "@/components/Brand";

// The Emergent-managed OAuth page is not customizable, so all CTAs go to
// our branded /login page first (Spanish + logo). The button there is the
// one that actually redirects to auth.emergentagent.com.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
function startGoogleLogin() {
    window.location.href = "/login?next=%2Feditor";
}

export default function Landing() {
    return (
        <div
            data-testid={LANDING.root}
            className="min-h-[100dvh] bg-[var(--bg)]"
        >
            {/* Top bar */}
            <header className="px-6 md:px-12 py-6 flex items-center justify-between">
                <a
                    href="/"
                    className="flex items-center gap-2.5 shrink-0"
                    data-testid="landing-brand"
                >
                    <BrandIcon
                        variant="dark"
                        style={{ height: 32, width: "auto" }}
                    />
                    <BrandLogo
                        variant="dark"
                        style={{ height: 16, width: "auto" }}
                        className="hidden sm:block"
                    />
                </a>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={startGoogleLogin}
                        className="btn-ghost"
                        data-testid="landing-nav-login"
                    >
                        Acceder
                    </button>
                    <button
                        type="button"
                        onClick={startGoogleLogin}
                        className="btn-primary"
                        data-testid="landing-nav-editor"
                        style={{ padding: "10px 18px", fontSize: 13 }}
                    >
                        Empieza ahora
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="px-6 md:px-12 pt-6 pb-20 md:pt-16 md:pb-24">
                <div className="max-w-[1200px] mx-auto grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
                    <div className="tpl-anim">
                        <h1
                            className="font-serif leading-[0.98]"
                            style={{ fontSize: "clamp(40px, 7vw, 76px)" }}
                        >
                            La forma más fácil de{" "}
                            <em className="italic opacity-90">
                                organizar
                            </em>{" "}
                            vuestra boda.
                        </h1>
                        <p
                            className="mt-5 text-[var(--ink-soft)] max-w-[520px]"
                            style={{ fontSize: "clamp(15px, 2vw, 17px)", lineHeight: 1.55 }}
                        >
                            Cread vuestra web de boda en minutos. Sin
                            plantillas recargadas, sin anuncios, sin
                            complicaciones. Solo vosotros y lo que importa.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={startGoogleLogin}
                                data-testid={LANDING.cta}
                                className="btn-primary"
                                style={{ padding: "18px 28px", fontSize: 16 }}
                            >
                                Crear mi web de boda gratis
                                <ArrowRight size={16} />
                            </button>
                            <div className="text-xs text-[var(--ink-soft)]">
                                Empieza gratis. Publica por 39 €.
                            </div>
                        </div>
                        <div className="mt-10 flex items-center gap-6 opacity-80">
                            <Feature icon={Heart} label="Pago único" />
                            <Feature icon={MapPin} label="Google Maps" />
                            <Feature icon={Clock} label="Cuenta atrás" />
                        </div>
                    </div>

                    {/* Hero phone mockups */}
                    <div className="relative md:h-[640px] flex items-center justify-center">
                        <div className="grid grid-cols-2 gap-4 md:gap-8 w-full max-w-[520px]">
                            <TemplatePhone
                                template="classic-elegant"
                                testId={LANDING.templatePreviewClassic}
                                tilt={-3}
                            />
                            <TemplatePhone
                                template="minimal-modern"
                                testId={LANDING.templatePreviewMinimal}
                                tilt={4}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Templates section */}
            <section className="px-6 md:px-12 pb-24 border-t border-[var(--line)] pt-20">
                <div className="max-w-[1100px] mx-auto">
                    <div className="text-center mb-14">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">
                            Dos estilos para empezar
                        </div>
                        <h2
                            className="font-serif mt-2"
                            style={{ fontSize: "clamp(32px, 5.5vw, 56px)" }}
                        >
                            Elegid el que <em className="italic">os represente</em>.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                        {TEMPLATES.map((tpl) => (
                            <div key={tpl.id} className="card-soft p-7">
                                <div className="flex items-center gap-1.5 mb-3">
                                    {tpl.swatch.map((c) => (
                                        <span
                                            key={c}
                                            className="inline-block w-4 h-4 rounded-full border"
                                            style={{
                                                background: c,
                                                borderColor:
                                                    "rgba(20,16,12,0.08)",
                                            }}
                                        />
                                    ))}
                                </div>
                                <h3 className="font-serif text-3xl leading-tight">
                                    {tpl.name}
                                </h3>
                                <p className="text-sm text-[var(--ink-soft)] mt-2">
                                    {tpl.description}
                                </p>
                                <button
                                    type="button"
                                    onClick={startGoogleLogin}
                                    className="btn-ghost mt-5 inline-flex"
                                    data-testid={`landing-try-${tpl.id}`}
                                >
                                    Probar esta plantilla
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="px-6 md:px-12 py-10 border-t border-[var(--line)] text-center">
                <BrandLogo
                    variant="dark"
                    style={{ height: 22, width: "auto" }}
                    className="mx-auto"
                />
                <div className="text-xs text-[var(--ink-soft)] mt-3">
                    Hecho con cariño para parejas que celebran el amor en
                    todas sus formas.
                </div>
                <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-[var(--ink-soft)] uppercase tracking-[0.18em]">
                    <a
                        href="/terms"
                        className="hover:text-[var(--ink)] transition-colors"
                        data-testid="footer-terms-link"
                    >
                        Términos
                    </a>
                    <span aria-hidden="true">·</span>
                    <a
                        href="/privacy"
                        className="hover:text-[var(--ink)] transition-colors"
                        data-testid="footer-privacy-link"
                    >
                        Privacidad
                    </a>
                </div>
                <div className="text-[10px] text-[var(--ink-soft)] mt-4 tracking-[0.22em] uppercase">
                    © {new Date().getFullYear()} Loving Angels
                </div>
            </footer>
        </div>
    );
}

function Feature({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2 text-xs text-[var(--ink-soft)]">
            <Icon size={14} />
            <span>{label}</span>
        </div>
    );
}

function TemplatePhone({ template, testId, tilt = 0 }) {
    const tplClass =
        template === "minimal-modern" ? "tpl-minimal" : "tpl-classic";
    const isClassic = template === "classic-elegant";
    const cover = isClassic
        ? "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=800&q=80"
        : "https://images.unsplash.com/photo-1525258946800-98cfd641d0de?auto=format&fit=crop&w=800&q=80";
    const primary = isClassic ? "Lucía" : "Sofía";
    const secondary = isClassic ? "Mateo" : "Alex";
    const dateStr = isClassic ? "12 de junio" : "07 . 09 . 2026";

    return (
        <div
            data-testid={testId}
            className="relative"
            style={{ transform: `rotate(${tilt}deg)` }}
        >
            <div className="phone-frame mx-auto" style={{ width: "100%", maxWidth: 240, height: 480 }}>
                <div className={`phone-screen ${tplClass}`}>
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(180deg, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.55) 100%), url(${cover}) center/cover`,
                        }}
                    />
                    <div className="absolute inset-0 flex flex-col justify-between p-4 text-white">
                        <div className="flex items-center justify-between">
                            <div
                                className="text-xs"
                                style={{ letterSpacing: "0.18em" }}
                            >
                                {primary.charAt(0)} &amp; {secondary.charAt(0)}
                            </div>
                            <div className="w-7 h-7 rounded-full border border-white/40 flex items-center justify-center">
                                <div className="flex flex-col gap-[3px]">
                                    <span className="w-3 h-px bg-white"></span>
                                    <span className="w-3 h-px bg-white"></span>
                                    <span className="w-3 h-px bg-white"></span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.28em] opacity-90">
                                Nos casamos
                            </div>
                            <div
                                className="display mt-1"
                                style={{
                                    fontSize: 28,
                                    lineHeight: 1.04,
                                }}
                            >
                                {primary}
                                <span className="opacity-70"> &amp; </span>
                                <br />
                                {secondary}
                            </div>
                            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] bg-white/15 backdrop-blur rounded-full px-2 py-1">
                                <Clock size={10} />
                                {dateStr}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
